import React from 'react';
import { Plus, Edit, Trash2, ChevronDown, ChevronRight } from 'lucide-react';

const OrganizationsSection = ({ 
  organizations, 
  expandedOrgs, 
  setExpandedOrgs,
  setEditingOrg,
  setOrgForm,
  setShowOrgModal,
  handleEditOrg,
  handleDeleteOrg,
  toggleExpanded,
  // Modal props
  showOrgModal,
  editingOrg,
  orgForm,
  orgLoading,
  handleOrgSubmit,
  handleOrgInputChange
}) => {
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Manage Organizations</h2>
        <button 
          onClick={() => {
            setEditingOrg(null);
            setOrgForm({
              organization_type: '',
              title: '',
              description: '',
              members: '',
              announcements: ''
            });
            setShowOrgModal(true);
          }}
          className="bg-green-600 text-white px-3 sm:px-4 py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center text-sm sm:text-base">
          <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
          Add Organization
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Organization Type
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {organizations.map((org) => (
                <tr key={org.id}>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{org.organization_type}</div>
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{org.title}</div>
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button 
                        onClick={() => handleEditOrg(org)}
                        className="text-blue-600 hover:text-blue-900 p-1">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleDeleteOrg(org.id)}
                        className="text-red-600 hover:text-red-900 p-1">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Expandable List View */}
        <div className="md:hidden">
          {organizations.map((org) => {
            const isExpanded = expandedOrgs.has(org.id);
            return (
              <div key={org.id} className="border-b border-gray-200 last:border-b-0">
                <div 
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                  onClick={() => toggleExpanded(org.id, expandedOrgs, setExpandedOrgs)}
                >
                  <div className="flex-1">
                    <div className="text-xs text-gray-500 uppercase tracking-wider">{org.organization_type}</div>
                    <h3 className="text-sm font-medium text-gray-900">{org.title}</h3>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="flex space-x-1">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditOrg(org);
                        }}
                        className="text-blue-600 hover:text-blue-900 p-1">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteOrg(org.id);
                        }}
                        className="text-red-600 hover:text-red-900 p-1">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
                {isExpanded && (
                  <div className="px-4 pb-4 space-y-2">
                    {org.description && (
                      <div>
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Description:</span>
                        <p className="text-sm text-gray-900 mt-1">{org.description}</p>
                      </div>
                    )}
                    {org.members && (
                      <div>
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Members:</span>
                        <p className="text-sm text-gray-900 mt-1">{org.members}</p>
                      </div>
                    )}
                    {org.announcements && (
                      <div>
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Announcements:</span>
                        <p className="text-sm text-gray-900 mt-1">{org.announcements}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Organization Modal */}
      {showOrgModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-md mx-auto max-h-[97%] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">{editingOrg ? 'Edit Organization' : 'Add New Organization'}</h3>
            <form onSubmit={handleOrgSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Organization Type *</label>
                <select
                  name="organization_type"
                  value={orgForm.organization_type}
                  onChange={handleOrgInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="">Select Organization Type</option>
                  <option value="Sunday School">Sunday School</option>
                  <option value="MMVS">MMVS</option>
                  <option value="MGOCSM">MGOCSM</option>
                  <option value="Managing Committee">Managing Committee</option>
                  <option value="Prayer Group">Prayer Group</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  name="title"
                  value={orgForm.title}
                  onChange={handleOrgInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  name="description"
                  value={orgForm.description}
                  onChange={handleOrgInputChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="Enter organization description (optional)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Members</label>
                <textarea
                  name="members"
                  value={orgForm.members}
                  onChange={handleOrgInputChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="Enter organization members (optional)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Announcements</label>
                <textarea
                  name="announcements"
                  value={orgForm.announcements}
                  onChange={handleOrgInputChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="Enter announcements (optional)"
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={orgLoading}
                  className="flex-1 bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {orgLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {editingOrg ? 'Updating...' : 'Adding...'}
                    </>
                  ) : (
                    editingOrg ? 'Update Organization' : 'Add Organization'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowOrgModal(false);
                    setEditingOrg(null);
                    setOrgForm({
                      organization_type: '',
                      title: '',
                      description: '',
                      members: '',
                      announcements: ''
                    });
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationsSection;